<template>
    <header v-if="navbar" class="header is-sticky" :class="{'theme-strategy': theme === 'strategy'}">
        <div id="mobile-nav" :class="{'mobile-content': true, 'menu-open': isSearchOpen}">
            <NavbarUnloggedMobile
                :navbar="navbar"
                :search-is-visible="searchIsVisible"
                :is-search-page="isSearchPage"
                :slices="slices"
                :header-search-slice="headerSearchSlice"
                :country-slice="countrySlice"
                :is-menu-open="isMenuOpen"
                :is-search-open="isSearchOpen"
                :has-theme-strategy="hasThemeStrategy"
                :logo-href="logoHref"
                @toggle-search-modal="toggleSearchModal"
                @toggle-burger-menu="toggleMenu"
            />
        </div>
        <div class="desktop-content">
            <div class="header-links">
                <a :href="logoHref">
                    <MaltImg :src="logoUrl" :alt="logoAlt" :width="logoSizes.desktop.width" :height="logoSizes.desktop.height" class="header__logo" />
                </a>
                <template v-if="!isSearchPage && !loggedIn">
                    <div class="header-megamenus">
                        <SliceZone :slices="slices.filter(filterFactory('megamenu'))" :components="components" />
                    </div>
                    <SliceZone :slices="slices.filter(filterFactory('header_links_slice'))" :components="components" />
                </template>
            </div>

            <div id="header-customized-zone" class="header-customized-zone" data-testid="header-customized-zone">
                <div id="header-customized-zone-search"></div>
                <div v-if="isSearchPage && headerSearchSlice" class="search-banner" data-testid="search-banner">
                    <SliceZone :slices="[headerSearchSlice]" :components="components" />
                </div>
                <HeaderSearch
                    v-else-if="showHeaderSearchButton"
                    v-show="searchIsVisible"
                    :header-search-slice="headerSearchSlice"
                    :search-placeholder-text="navbar?.data.searchPlaceholderText"
                />
                <div class="header-user-slice-wrapper">
                    <SliceZone :slices="slices.filter(filterFactory('header_user_slice'))" :components="components" />
                </div>
                <SliceZone :slices="slices.filter((slice) => slice.slice_type === 'user_settings_slice')" :components="components" />
                <LazyLocaleSelection />
            </div>
        </div>
    </header>
</template>

<script setup lang="ts">
    import {computed, onBeforeUnmount, onMounted, provide, ref, watch} from 'vue';
    import {navbarEventBus} from '@navbar/events';
    import {useSearchRouteDefinitions} from '@navbar/features/search';
    import HeaderSearch from '@navbar-unlogged/components/HeaderSearch.vue';
    import {useNavbarUser, usePrismicDocument} from '@navbar-unlogged/composables';
    import {navbarUnloggedKeys} from '@navbar-unlogged/keys';
    import {components} from '@navbar-unlogged/slices';
    import type {PrismicDocument, SharedSlice} from '@prismicio/types';
    import {SliceZone} from '@prismicio/vue';
    import type {CountrySliceSlice, HeaderSearchSliceSlice} from '@prismicio-types';
    import {LazyLocaleSelection, MaltImg} from '#components';
    import {useAsyncData, useHead, useLocalePrefix, usePrismicLang, useRoute} from '#imports';
    import NavbarUnloggedMobile from './components/navbarUnlogged/NavbarUnloggedMobile.vue';

    type Theme = 'default' | 'strategy';

    const props = withDefaults(
        defineProps<{
            showSearch?: boolean;
            prismicDocument?: PrismicDocument;
            theme?: Theme;
        }>(),
        {
            showSearch: true,
            theme: 'default',
        },
    );

    // Composables
    const {loggedIn, isCompany} = useNavbarUser();
    const {currentPage} = useSearchRouteDefinitions();
    const {lang} = usePrismicLang();
    const {withLocalePrefix} = useLocalePrefix();

    // Refs
    const isSearchOpen = ref(false);
    const isMenuOpen = ref(false);

    const logoUrl = ref('/cms-front/maltLogo.png');
    const logoAlt = ref('Malt Logo');
    const logoSizes = ref({desktop: {width: 110, height: 32}});

    if (props.theme === 'strategy') {
        logoUrl.value = '/cms-front/malt-strategy-logo.png';
        logoAlt.value = 'Malt Strategy Logo';
        logoSizes.value = {desktop: {width: 180, height: 28}};
    }

    const route = useRoute();

    const logoHref = computed(() => withLocalePrefix(props.theme === 'strategy' ? '/c/maltstrategy' : '/'));

    const navbar = ref<PrismicDocument | undefined | null>(props.prismicDocument);
    if (!navbar.value) {
        const {data} = await useAsyncData('malt-header', () => usePrismicDocument('malt-header', lang.value), {deep: true});
        navbar.value = data.value;
    }
    provide(navbarUnloggedKeys.PRISMIC_DOCUMENT, navbar.value!);

    // Computed Slices
    const slices = computed<SharedSlice[]>(() => navbar.value?.data?.slices || []);
    const countrySlice = computed<CountrySliceSlice>(() => slices.value.find(filterFactory('country_slice')) as CountrySliceSlice);
    const headerSearchSlice = computed<HeaderSearchSliceSlice>(
        () => slices.value.find(filterFactory('header_search_slice')) as HeaderSearchSliceSlice,
    );

    // Computed values
    const showHeaderSearchButton = computed(() => !loggedIn.value || isCompany.value);
    const hasThemeStrategy = computed(() => props.theme === 'strategy');

    const searchIsVisible = computed(() => navbar.value?.data.pageWithSearch && props.showSearch);

    const isSearchPage = computed(() => currentPage.value?.name === 'Search');
    const bodyStyle = computed(() => (isSearchOpen.value ? 'overflow: hidden;' : ''));

    useHead({
        bodyAttrs: {
            style: bodyStyle,
        },
    });

    // Handlers
    function toggleMenu() {
        isMenuOpen.value = !isMenuOpen.value;
        isSearchOpen.value = false;
    }

    function toggleSearchModal() {
        isSearchOpen.value = !isSearchOpen.value;
        isMenuOpen.value = false;
    }

    function filterFactory(sliceType: string): (slice: SharedSlice) => boolean {
        return (slice: SharedSlice) => slice.slice_type === sliceType;
    }

    function onSearchRequested() {
        isSearchOpen.value = false;
    }

    // Watchers
    watch(
        () => route.name,
        () => {
            isMenuOpen.value = false;
        },
    );

    // Lifecycle hooks
    onMounted(() => {
        navbarEventBus.on('navbar-search-requested', onSearchRequested);
    });

    onBeforeUnmount(() => {
        navbarEventBus.off('navbar-search-requested', onSearchRequested);
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@navbar-unlogged/scss/variables.scss' as vars;

    .header {
        width: 100%;
        background: var(--joy-color-background-default);
        border-bottom: solid 1px var(--joy-color-neutral-30);
        font-family: var(--joy-font-family-base);
        padding: 6px var(--joy-core-spacing-9);

        &__logo {
            display: block;
        }

        &.is-sticky {
            position: sticky;
            top: 0;
            left: 0;
            z-index: var(--joy-core-z-index-fixed-content);
            height: 72px;

            @include mq.screen_md {
                padding: 0 var(--joy-core-spacing-7) 0 var(--joy-core-spacing-4);
            }

            @include mq.screen_sm {
                height: 56px;
            }

            @include mq.screen_xxs {
                padding: 0 var(--joy-core-spacing-4);
            }
        }

        @include mq.screen_md {
            padding: 6px var(--joy-core-spacing-7);
        }

        @include mq.screen_sm {
            height: 56px;
            padding: 6px var(--joy-core-spacing-3);
        }

        .desktop-content {
            text-align: center;

            @include vars.flex-row-align-center;

            place-content: center space-between;
            height: 100%;

            @include mq.screen_md {
                display: none;
            }

            .header-megamenus {
                display: flex;
                justify-content: flex-start;
            }

            .header-customized-zone {
                display: flex;
                justify-content: flex-end;
                gap: 12px;
                height: 40px;
            }
        }

        .mobile-content {
            @include vars.flex-row-align-center;

            justify-content: space-between;
            height: 100%;
            display: none;

            @include mq.screen_md {
                display: flex;
            }

            .button-placeholder {
                width: 36px;
            }
        }
    }

    :deep(.modale) {
        position: fixed;
        inset: var(--header-height) 0 0 0;
        display: none;
        opacity: 0;
        background-color: var(--joy-color-background-default);
        padding: 10px 20px 40px;
        z-index: var(--joy-core-z-index-dialog);
        overflow: auto;

        &.open {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            opacity: 1;
            animation: fade-in var(--joy-transition-duration-default);
        }

        .freelance-search {
            box-shadow: none;
            padding: 0;
        }
    }

    .header-links {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .search-banner {
        position: absolute;
        top: var(--header-height);
        left: 0;
        width: 100%;
        height: var(--search-banner-unlogged-height);
        margin: 0;
        padding: 0;
        background-color: var(--joy-color-background-default);
        border-bottom: solid 1px var(--joy-color-neutral-30);

        :global(:root) {
            --search-banner-unlogged-height: 72px;

            @include mq.screen_md {
                --search-banner-unlogged-height: 0px;
            }
        }
    }
</style>
